import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '../common/Alert.js';
import loanReceivedService from '../../services/LoanReceivedService.js';
import { CheckCircle, ArrowLeft, Pencil, Trash2, XCircle, CreditCard, X } from 'lucide-react';
import { PageLayout, Button, StatusBadge } from '../common/index.js';

const ViewLoanReceived = () => {
  const { loanId } = useParams();
  const [loan, setLoan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchLoan();
  }, [loanId]);

  const fetchLoan = async () => {
    try {
      setLoading(true);
      const response = await loanReceivedService.getById(loanId);
      setLoan(response.data);
    } catch (err) {
      Alert.error('Failed to load loan data');
      console.error(err);
      navigate('/loans-received');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (window.confirm('Are you sure you want to approve this loan?')) {
      try {
        await loanReceivedService.approve(loanId);
        Alert.success('Loan approved successfully');
        fetchLoan();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to approve loan');
      }
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      Alert.error('Please provide a rejection reason');
      return;
    }
    try {
      await loanReceivedService.reject(loanId, rejectionReason);
      Alert.success('Loan rejected successfully');
      setShowRejectModal(false);
      setRejectionReason('');
      fetchLoan();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to reject loan');
    }
  };

  const handleActivate = async () => {
    if (window.confirm('Are you sure you want to activate this loan?')) {
      try {
        await loanReceivedService.activate(loanId);
        Alert.success('Loan activated successfully');
        fetchLoan();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to activate loan');
      }
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this loan? This action cannot be undone.')) {
      try {
        await loanReceivedService.delete(loanId);
        Alert.success('Loan deleted successfully');
        navigate('/loans-received');
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete loan');
      }
    }
  };

  const viewAgreement = () => {
    const documentUrl = loanReceivedService.getLoanAgreement(loanId);
    window.open(documentUrl, '_blank');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
      approved: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Approved' },
      active: { bg: 'bg-green-100', text: 'text-green-800', label: 'Active' },
      completed: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Completed' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejected' },
      defaulted: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Defaulted' },
      cancelled: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Cancelled' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span
        className={`px-3 py-1 text-sm font-semibold rounded-sm 
                    inline-flex items-center justify-center
                    ${config.bg} ${config.text}`}
      >
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <PageLayout title="Loan Details" icon={CreditCard}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-sm text-gray-600">Loading loan data...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!loan) return null;

  return (
    <>
    <PageLayout
      title={loan.loan_id}
      icon={CreditCard}
      actions={
        <>
          {getStatusBadge(loan.status)}
          {loan.is_overdue && <StatusBadge variant="danger" label="Overdue" />}
          {loan.status === 'pending' && (
            <>
              <Button icon={CheckCircle} onClick={handleApprove}>Approve Loan</Button>
              <Button variant="danger" icon={XCircle} onClick={() => setShowRejectModal(true)}>Reject Loan</Button>
              <Button variant="secondary" icon={Pencil} onClick={() => navigate(`/loans-received/edit/${loanId}`)}>Edit</Button>
              <Button variant="danger" icon={Trash2} onClick={handleDelete}>Delete</Button>
            </>
          )}
          {loan.status === 'approved' && (
            <>
              <Button onClick={handleActivate}>Activate Loan</Button>
              <Button onClick={() => navigate(`/loans-received/repayment/${loanId}`)}>Add Repayment</Button>
            </>
          )}
          {loan.status === 'active' && (
            <Button onClick={() => navigate(`/loans-received/repayment/${loanId}`)}>Add Repayment</Button>
          )}
          {loan.has_agreement && (
            <Button variant="outline" onClick={viewAgreement}>View Agreement</Button>
          )}
          <Button variant="outline" icon={ArrowLeft} onClick={() => navigate('/loans-received')}>Back to List</Button>
        </>
      }
    >
                {/* Summary Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
                  <div className="bg-white p-4 rounded-sm shadow-sm border-l-4 border-brand">
                    <p className="text-xs text-gray-600 mb-1">Loan Amount</p>
                    <p className="text-lg sm:text-xl font-bold text-brand">{formatCurrency(loan.loan_amount)}</p>
                  </div>
                  <div className="bg-white p-4 rounded-sm shadow-sm border-l-4 border-green-500">
                    <p className="text-xs text-gray-600 mb-1">Total Amount</p>
                    <p className="text-lg sm:text-xl font-bold text-green-600">{formatCurrency(loan.total_amount)}</p>
                  </div>
                  <div className="bg-white p-4 rounded-sm shadow-sm border-l-4 border-brand">
                    <p className="text-xs text-gray-600 mb-1">Amount Paid</p>
                    <p className="text-lg sm:text-xl font-bold text-blue-600">{formatCurrency(loan.amount_paid)}</p>
                  </div>
                  <div className="bg-white p-4 rounded-sm shadow-sm border-l-4 border-red-500">
                    <p className="text-xs text-gray-600 mb-1">Amount Pending</p>
                    <p className="text-lg sm:text-xl font-bold text-red-600">{formatCurrency(loan.amount_pending)}</p>
                  </div>
                </div>

                {/* Tabs */}
                <div className="bg-white rounded-sm shadow-sm mb-4">
                  <div className="border-b flex overflow-x-auto">
                    <button
                      onClick={() => setActiveTab('details')}
                      className={`px-4 sm:px-6 py-3 text-sm font-medium whitespace-nowrap ${
                        activeTab === 'details'
                          ? 'border-b-2 border-brand text-blue-600'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Loan Details
                    </button>
                    <button
                      onClick={() => setActiveTab('lender')}
                      className={`px-4 sm:px-6 py-3 text-sm font-medium whitespace-nowrap ${
                        activeTab === 'lender'
                          ? 'border-b-2 border-brand text-blue-600'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Lender Info
                    </button>
                    <button
                      onClick={() => setActiveTab('repayments')}
                      className={`px-4 sm:px-6 py-3 text-sm font-medium whitespace-nowrap ${
                        activeTab === 'repayments'
                          ? 'border-b-2 border-brand text-blue-600'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Repayments ({loan.repayments_count || 0})
                    </button>
                  </div>
                </div>

                {/* Tab Content */}
                <div className="bg-white rounded-sm shadow-sm p-4 sm:p-6">
                  {activeTab === 'details' && (
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Loan Information</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-8">
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Loan Type</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-words">{loan.loan_type.replace('_', ' ')}</p>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Loan Amount</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-words">{formatCurrency(loan.loan_amount)}</p>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Interest Rate</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-words">{loan.interest_rate}% per annum</p>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Processing Fee</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-words">{formatCurrency(loan.processing_fee)}</p>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Loan Date</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-words">{formatDate(loan.loan_date)}</p>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Due Date</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-words">{formatDate(loan.due_date)}</p>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Disbursed Date</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-words">{formatDate(loan.disbursed_date)}</p>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Loan Account Number</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-words">{loan.loan_account_number || 'N/A'}</p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">Repayment Details</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Repayment Frequency</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-words">{loan.repayment_frequency.replace('_', ' ')}</p>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Total Installments</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-words">{loan.total_installments}</p>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Installment Amount</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-words">{formatCurrency(loan.installment_amount)}</p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">Additional Information</h3>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Purpose</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-words">{loan.purpose}</p>
                          </div>
                          {loan.collateral_details && (
                            <div>
                              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Collateral Details</label>
                              <p className="text-sm sm:text-base text-secondary font-semibold break-words">{loan.collateral_details}</p>
                            </div>
                          )}
                          {loan.remarks && (
                            <div>
                              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Remarks</label>
                              <p className="text-sm sm:text-base text-secondary font-semibold break-words">{loan.remarks}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {loan.status === 'rejected' && loan.rejection_reason && (
                        <div className="bg-red-50 border border-red-200 rounded-sm p-4">
                          <label className="block text-sm font-medium text-red-800 mb-1">Rejection Reason</label>
                          <p className="text-base text-red-900">{loan.rejection_reason}</p>
                        </div>
                      )}

                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">Audit Information</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Created By</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-words">{loan.created_by}</p>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Created At</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-words">{formatDateTime(loan.created_at)}</p>
                          </div>
                          {loan.approved_by && (
                            <>
                              <div>
                                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Approved By</label>
                                <p className="text-sm sm:text-base text-secondary font-semibold break-words">{loan.approved_by}</p>
                              </div>
                              <div>
                                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Approved Date</label>
                                <p className="text-sm sm:text-base text-secondary font-semibold break-words">{formatDateTime(loan.approved_date)}</p>
                              </div>
                            </>
                          )}
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Last Updated</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-words">{formatDateTime(loan.updated_at)}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'lender' && (
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">Lender Details</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Lender Type</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-words">{loan.lender_type.replace('_', ' ')}</p>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Lender Name</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-words">{loan.lender_name}</p>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Contact Number</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-words">{loan.lender_contact}</p>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Email</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-words">{loan.lender_email || 'N/A'}</p>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Account Number</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-words">{loan.account_number || 'N/A'}</p>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">IFSC Code</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-words">{loan.ifsc_code || 'N/A'}</p>
                          </div>
                          {loan.lender_address && (
                            <div className="sm:col-span-2">
                              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Address</label>
                              <p className="text-sm sm:text-base text-secondary font-semibold break-words">{loan.lender_address}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'repayments' && (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Repayment History</h3>
                        {(loan.status === 'approved' || loan.status === 'active') && (
                          <button
                            onClick={() => navigate(`/loans-received/repayment/${loanId}`)}
                            className="px-4 py-2 bg-green-500 text-white rounded-sm font-medium hover:bg-green-600 transition-colors text-sm"
                          >
                            Add Repayment
                          </button>
                        )}
                      </div>

                      {!loan.repayments || loan.repayments.length === 0 ? (
                        <div className="text-center py-8">
                          <p className="text-gray-600">No repayments recorded yet</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {loan.repayments.map((repayment) => (
                            <div key={repayment.repayment_id} className="border border-gray-200 rounded-sm p-4 hover:bg-gray-50">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                                <div>
                                  <h4 className="font-semibold text-gray-900">{repayment.repayment_id}</h4>
                                  <p className="text-sm text-gray-600">{formatDate(repayment.payment_date)}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xl font-bold text-green-600">{formatCurrency(repayment.amount)}</p>
                                  <p className="text-sm text-gray-600 capitalize">{repayment.payment_mode.replace('_', ' ')}</p>
                                </div>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 text-sm">
                                {repayment.transaction_id && (
                                  <div>
                                    <span className="text-gray-600">Transaction ID:</span>
                                    <p className="font-medium text-gray-900">{repayment.transaction_id}</p>
                                  </div>
                                )}
                                <div>
                                  <span className="text-gray-600">Paid By:</span>
                                  <p className="font-medium text-gray-900">{repayment.paid_by}</p>
                                </div>
                                {repayment.remarks && (
                                  <div className="sm:col-span-2">
                                    <span className="text-gray-600">Remarks:</span>
                                    <p className="font-medium text-gray-900">{repayment.remarks}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
    </PageLayout>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Reject Loan</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rejection Reason *
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter reason for rejection"
                rows="4"
                className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleReject}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-sm font-medium hover:bg-red-600 transition-colors"
              >
                Reject Loan
              </button>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ViewLoanReceived;